import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLang } from "../i18n";

const HERO_IMG = "https://images.unsplash.com/photo-1623408861409-b58fb2d60315?auto=format&fit=crop&w=2000&q=80";

const MaskedLine = ({ children, delay }) => (
  <span className="mask-line">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const scrollTo = (hash) => (e) => {
    e.preventDefault();
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden bg-coal">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -z-0">
        <motion.img
          src={HERO_IMG}
          alt="Gevgelija at dusk"
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
          className="w-full h-[115%] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-coal/90 via-coal/30 to-coal/40" />
      </motion.div>

      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 h-full max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-24 md:pb-28">
        <h1 className="font-serif text-sand leading-[0.95] tracking-tight text-[13.5vw] sm:text-6xl md:text-7xl lg:text-[6.5rem] font-medium">
          <MaskedLine delay={0.65}>{t("hero.line1")}</MaskedLine>
          <MaskedLine delay={0.8}>
            <em className="font-light italic text-sand/85">{t("hero.line2")}</em>
          </MaskedLine>
          <MaskedLine delay={0.95}>
            <span className="text-ember">{t("hero.line3")}</span>
          </MaskedLine>
        </h1>

        <div className="mt-8 md:mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.25 }}
            className="text-sand/75 text-sm md:text-base leading-relaxed max-w-md"
          >
            {t("hero.sub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.4 }}
            className="flex flex-wrap gap-4"
          >
            <a
              href="#apartments"
              data-testid="hero-explore-cta"
              onClick={scrollTo("#apartments")}
              className="inline-flex items-center px-8 py-4 bg-terra text-sand text-xs font-bold tracking-[0.2em] uppercase hover:bg-ember hover:text-coal transition-colors duration-300"
            >
              {t("hero.ctaExplore")}
            </a>
            <a
              href="#contact"
              data-testid="hero-contact-cta"
              onClick={scrollTo("#contact")}
              className="inline-flex items-center px-8 py-4 border border-sand/40 text-sand text-xs font-bold tracking-[0.2em] uppercase hover:bg-sand hover:text-coal transition-colors duration-300"
            >
              {t("hero.ctaContact")}
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-sand/60"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">{t("hero.scroll")}</span>
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown className="w-4 h-4" strokeWidth={1.5} />
        </motion.div>
      </motion.div>
    </section>
  );
};
